/**
 * Holiday packages API service.
 *
 * Unlike the rest of the app (form-encoded posts to v2dispatch.jsp), packages
 * is a JSON REST service:
 *
 *   POST {origin}/api/nextra/packages/v1/{operation}
 *   headers: Content-Type: application/json, authtoken: <session token>
 *
 * Every operation answers the same flat envelope:
 *   { Status: 'OK' | 'NOK', StatusCode: '200', Message, Operation,
 *     TransactionStatus: 'SUCCESS' | 'FAILURE', Result }
 *
 *   available-facets  {}                                   -> Result { destinations, priceMin/Max, durationMin/Max, months }
 *   search            { destination, minDays, maxDays, maxPrice, sort, pax, searchId }
 *                                                          -> Result { searchId, total, packages }
 *   detail            { packageKey, searchId }             -> Result.package
 *   quote             { packageKey, priceKey, departureKey, pax, searchId }
 *                                                          -> Result.package (nextraQuoteId, fare)
 *   book              { packageKey, clientBookingId, searchId, nextraQuoteId,
 *                       priceKey, departureKey, pax, passengers, specialRequests }
 *                                                          -> Result (the booking, flat)
 *   booking-details   { pkgBookingId }                     -> Result.packageMain
 *   enquire           { packageKey, clientBookingId, searchId, passengers }
 *
 * `pax` counts PEOPLE per sharing type: { twin: 2 } is two adults in one twin
 * room. Keys: twin, single, triple, child_with_bed, child_no_bed, infant.
 *
 * book debits the agent's wallet (it answers paymentStatus "paid"), so it is
 * never retried automatically and never sent with an abort signal.
 */

import {
  APP_MAIN_URL,
  APP_USER_AUTH,
  APP_WEB_URL,
} from '../../../saas/AppStorageStaticNames';
import {
  clearLocalStorage,
  getStringFromLS,
} from '../../../shared/helper-utils/localStorage';
import { PACKAGE_API } from '../constants/packageConstants';

/** https://host/ of the configured API, whatever path the main URL carries. */
// eslint-disable-next-line no-unused-vars
const apiOrigin = () => {
  const candidates = [getStringFromLS(APP_MAIN_URL), APP_WEB_URL];
  for (let i = 0; i < candidates.length; i += 1) {
    try {
      if (candidates[i]) return new URL(candidates[i]).origin;
    } catch (e) {
      // not an absolute URL — try the next one
    }
  }
  return '';
};

const endpoint = (op) =>
  `https://api.fastays.com/${PACKAGE_API.BASE_PATH}${op}`;

/**
 * POST JSON. Resolves the parsed body, or undefined when no usable answer
 * came back (offline, CORS, a non-JSON body, an abort).
 *
 * The app registers a global fetch interceptor (api/apiUtils.js) that already
 * parses the body, so `fetch` normally resolves the data itself; a plain
 * Response is handled too in case the interceptor is not registered.
 */
const postJson = async (op, body, signal) => {
  try {
    const res = await fetch(endpoint(op), {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        authtoken: getStringFromLS(APP_USER_AUTH) || '',
      },
      body: JSON.stringify(body || {}),
    });
    if (res && typeof res.json === 'function') return await res.json();
    return res;
  } catch (error) {
    if (error?.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error(`[packages] ${op} failed:`, error);
    }
    return undefined;
  }
};

/**
 * One reader for the envelope. `network: true` means there was no answer at
 * all. `message` is the server's own text, suitable to show to the agent.
 */
export const readPackageResult = (res) => {
  if (!res || typeof res !== 'object') {
    return {
      ok: false,
      network: true,
      data: null,
      message: '',
      statusCode: null,
      correlationId: '',
      expired: false,
    };
  }
  const statusCode = Number(res.StatusCode);
  const ok =
    String(res.Status).toUpperCase() === 'OK' &&
    statusCode >= 200 &&
    statusCode < 300 &&
    String(res.TransactionStatus || 'SUCCESS').toUpperCase() !== 'FAILURE' &&
    res.Result !== null &&
    res.Result !== undefined;

  // Same session-expiry contract as the dispatch APIs (see apiUtils.js).
  if (statusCode === 900) {
    clearLocalStorage();
    window.location.href = '/';
  }

  if (!ok) {
    // eslint-disable-next-line no-console
    console.warn(`[packages] ${res.Operation || ''} failed`, res.Message);
  }

  return {
    ok,
    network: false,
    data: ok ? res.Result : null,
    message: res.Message || '',
    statusCode: Number.isFinite(statusCode) ? statusCode : null,
    correlationId:
      (res.Result &&
        typeof res.Result === 'object' &&
        res.Result.correlationId) ||
      '',
    expired: !ok && statusCode === 410,
  };
};

let facetsPromise = null;

/**
 * What the catalogue holds right now (destinations with counts, price and
 * duration ranges). Cached for the life of the tab; a failure is not cached.
 * Resolves { ok, facets, read }.
 */
export const fetchPackageFacetsApi = () => {
  if (!facetsPromise) {
    facetsPromise = postJson(PACKAGE_API.OPS.FACETS, {}).then((res) => {
      const read = readPackageResult(res);
      const facets = read.ok ? read.data : null;
      if (!facets) facetsPromise = null;
      return { ok: Boolean(facets), facets, read };
    });
  }
  return facetsPromise;
};

/** -> read; read.data: { searchId, total, packages } */
export const searchPackagesApi = async (req, signal) =>
  readPackageResult(await postJson(PACKAGE_API.OPS.SEARCH, req, signal));

/** -> read; read.data.package */
export const packageDetailApi = async ({ packageKey, searchId }, signal) =>
  readPackageResult(
    await postJson(PACKAGE_API.OPS.DETAIL, { packageKey, searchId }, signal),
  );

/** -> read; read.data.package: { nextraQuoteId, fare, ... } */
export const packageQuoteApi = async (
  { packageKey, priceKey, departureKey, pax, searchId },
  signal,
) =>
  readPackageResult(
    await postJson(
      PACKAGE_API.OPS.QUOTE,
      { packageKey, priceKey, departureKey, pax, searchId },
      signal,
    ),
  );

/** No signal on purpose: a book must never be cancelled from the client. */
export const bookPackageApi = async (req) =>
  readPackageResult(await postJson(PACKAGE_API.OPS.BOOK, req));

/** -> read; read.data.packageMain */
export const packageBookingDetailsApi = async (pkgBookingId, signal) =>
  readPackageResult(
    await postJson(PACKAGE_API.OPS.BOOKING_DETAILS, { pkgBookingId }, signal),
  );

/** Send an enquiry instead of booking; nothing is charged. */
export const enquirePackageApi = async (req) =>
  readPackageResult(await postJson(PACKAGE_API.OPS.ENQUIRE, req));

/**
 * The agent's package bookings and enquiries booked between two dates
 * (yyyy-MM-dd, by booking date). Resolves { ok, transactions, message }:
 * ok is false only when the call itself failed, so an empty list is ok.
 */
export const getPackageTransactionsApi = async (fromDate, toDate, signal) => {
  const read = readPackageResult(
    await postJson(PACKAGE_API.OPS.TRANSACTIONS, { fromDate, toDate }, signal),
  );
  const list = read.data?.transactions;
  return {
    ok: read.ok,
    transactions: Array.isArray(list) ? list.filter(Boolean) : [],
    message: read.network
      ? 'Could not reach the server. Please try again.'
      : read.message,
  };
};
